import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import GrainOverlay from "@/components/GrainOverlay";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Section from "@/pages/Section";
import History from "@/pages/History";
import Keepsake from "@/pages/Keepsake";
import Pricing from "@/pages/Pricing";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import WhisperCircle from "@/pages/WhisperCircle";
import SealPage from "@/pages/SealPage";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import AdminDistress from "@/pages/AdminDistress";
import AdminAnnouncements from "@/pages/AdminAnnouncements";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminAffiliates from "@/pages/AdminAffiliates";
import AdminPricing from "@/pages/AdminPricing";
import AdminBroadcasts from "@/pages/AdminBroadcasts";
import AdminMarketing from "@/pages/AdminMarketing";
import AdminProviders from "@/pages/AdminProviders";
import AdminFounders from "@/pages/AdminFounders";
import AdminCancelSave from "@/pages/AdminCancelSave";
import AdminEbooks from "@/pages/AdminEbooks";
import Partner from "@/pages/Partner";
import Library from "@/pages/Library";
import PromoCard from "@/pages/PromoCard";
import Signup from "@/pages/Signup";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import FAQ from "@/pages/FAQ";
import AdminBlog from "@/pages/AdminBlog";
import AdminBlogEditor from "@/pages/AdminBlogEditor";
import FreeEbook from "@/pages/FreeEbook";
import About from "@/pages/About";
import Compare from "@/pages/Compare";
import TryChapterOne from "@/pages/TryChapterOne";
import AdminStories from "@/pages/AdminStories";
import AdminSite from "@/pages/AdminSite";
import ShareStory from "@/pages/ShareStory";
import BlogTopic from "@/pages/BlogTopic";
import PathSync from "@/components/PathSync";
import DailyNudge from "@/components/DailyNudge";
import AnnouncementModal from "@/components/AnnouncementModal";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import Footer from "@/components/Footer";
import A11yToggle from "@/components/A11yToggle";
import { AuthProvider } from "@/lib/auth";
import { SeasonProvider } from "@/lib/season";

// Constant so React's Toaster doesn't re-render on every parent render.
const TOAST_OPTIONS = {
  style: {
    background: "#292832",
    border: "1px solid #453D49",
    color: "#E6E1D9",
    fontFamily: "Manrope, sans-serif",
    borderRadius: "0",
  },
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous session_id detection (must run during render, not in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/promo-card" element={<PromoCard />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/page/:page" element={<Blog />} />
      <Route path="/blog/category/:category" element={<Blog />} />
      <Route path="/blog/category/:category/page/:page" element={<Blog />} />
      <Route path="/blog/tag/:tag" element={<Blog />} />
      <Route path="/blog/tag/:tag/page/:page" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/blog/preview/:slug" element={<BlogPost />} />
      <Route path="/blog/share/:slug" element={<BlogPost />} />
      <Route path="/free-ebook" element={<FreeEbook />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/about" element={<About />} />
      <Route path="/vs/:rival" element={<Compare />} />
      <Route path="/try" element={<TryChapterOne />} />
      <Route path="/share-story/:token" element={<ShareStory />} />
      <Route path="/blog/topic/:topic" element={<BlogTopic />} />
      <Route path="/seal/:sectionId" element={<SealPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/section/:sectionId"
        element={
          <ProtectedRoute>
            <Section />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        }
      />
      <Route
        path="/keepsake"
        element={
          <ProtectedRoute>
            <Keepsake />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whisper"
        element={
          <ProtectedRoute>
            <WhisperCircle />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/distress"
        element={
          <AdminRoute>
            <AdminDistress />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/announcements"
        element={
          <AdminRoute>
            <AdminAnnouncements />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <AdminRoute>
            <AdminAnalytics />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/affiliates"
        element={
          <AdminRoute>
            <AdminAffiliates />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/pricing"
        element={
          <AdminRoute>
            <AdminPricing />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/broadcasts"
        element={
          <AdminRoute>
            <AdminBroadcasts />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/marketing"
        element={
          <AdminRoute>
            <AdminMarketing />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/providers"
        element={
          <AdminRoute>
            <AdminProviders />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/founders"
        element={
          <AdminRoute>
            <AdminFounders />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/cancel-save"
        element={
          <AdminRoute>
            <AdminCancelSave />
          </AdminRoute>
        }
      />
      <Route
        path="/partner"
        element={
          <ProtectedRoute>
            <Partner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <Library />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/site"
        element={
          <AdminRoute>
            <AdminSite />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/stories"
        element={
          <AdminRoute>
            <AdminStories />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/ebooks"
        element={
          <AdminRoute>
            <AdminEbooks />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/blog"
        element={
          <AdminRoute>
            <AdminBlog />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/blog/new"
        element={
          <AdminRoute>
            <AdminBlogEditor />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/blog/edit/:id"
        element={
          <AdminRoute>
            <AdminBlogEditor />
          </AdminRoute>
        }
      />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-obsidian-900 text-bone-100">
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <GrainOverlay />
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SeasonProvider>
              <div id="main-content">
                <AppRouter />
              </div>
              <DailyNudge />
              <AnnouncementModal />
              <A11yToggle />
              <PathSync />
              <Footer />
            </SeasonProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
      <Toaster theme="dark" toastOptions={TOAST_OPTIONS} />
    </div>
  );
}

export default App;
